/*** IMPORTS FROM imports-loader ***/
var define = false;

/*jslint unparam:true, todo: true */

"use strict";

var ko = require("knockout");
var R = require("ramda");

var stringUtil = require("../util/string");

var aggregatorFactory = require("./aggregatorFactory");
var comparerFactory = require("./comparerFactory");
var formatterFactory = require("./formatterFactory");
var groupFactory = require("./groupFactory");
var slickData = require("./slick.dataview");
var util = require("./util");



//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Private
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

//-------------------------------------------
function findColumn(id, columns) {
    return R.find(R.propEq("id", id), columns);
}


//-------------------------------------------
// Looks at next items until a focusable row
// (not group or totals) is found.  If the end
// is reached it looks at previous items.
function findFocus(dataView, row) {
    var i, item, l = dataView.getLength();

    for (i = row; i < l; i += 1) {
        item = dataView.getItem(i);
        if (item && !item.__group && !item.__groupTotals) { return i; }
    }

    for (i = row - 1; i >= 0; i -= 1) {
        item = dataView.getItem(i);
        if (item && !item.__group && !item.__groupTotals) { return i; }
    }

    return null;
}


//-------------------------------------------
var isNullOrUndefined = R.either(R.identical(undefined), R.identical(null));


//-------------------------------------------
// Returns true if the value is an object.
var isObject = R.compose(R.identical("Object"), R.type);


//-------------------------------------------
// Adapts the formatter function to what
// slickgrid expects.
function adaptFormatter(formatter, groupFormatter) {
    if (typeof formatter !== "function") { throw new Error("formatter must be a function."); }
    return function adaptedFormatter(row, cell, value, columnDef, dataContext) {
        if (dataContext.__group && dataContext.displayHeaderTotals && dataContext.totals.__groupTotals) {
            return groupFormatter ? groupFormatter(dataContext.totals) : "";
        }
        if (dataContext.__groupTotals) {
            return groupFormatter ? groupFormatter(dataContext.group.totals) : "";
        }
        return formatter(dataContext);
    };
}

//-------------------------------------------
// Create a function that calls "targetFn"
// (preserving "this" context) and ensuring
// that "this._internalUpdate" will be
// true while "targetFn" is running (preventing
// execution of "_onObservableChanged").
function createInternalUpdateGuard(targetFn) {
    return function internalUpdateGuard() {
        var originalValue = this._internalUpdate;
        this._internalUpdate = true;
        try {
            return targetFn.apply(this, arguments);
        } finally {
            this._internalUpdate = originalValue;
        }
    };
}


//-------------------------------------------
// Create a function that ensures the items
// observable is notified.
function createItemsModifier(targetFn) {
    return function () {
        this.items._innerObservable.valueWillMutate();
        targetFn.apply(this, arguments);
        this.items._innerObservable.valueHasMutated();
    };
}


//-------------------------------------------
// Creates and returns readonly observable with
// a default initial value and private inner observable.
function createReadonlyObservable(defaultValue) {
    var o = ko.observable(defaultValue),
        c = ko.computed({
            read: function () { return o(); },
            write: function () { throw new Error("Use 'apply()' method to change grid view model"); }
        });
    c._innerObservable = o;
    return c;
}


//-------------------------------------------
// Transforms an array of columns to the
// slickgrid representation.
function mapColumns(columns, factories) {
    if (!Array.isArray(columns)) { throw new Error("columns must be an array."); }
    var lookup = { };
    return R.map(function mapColumn(column) {
        // Validate
        if (stringUtil.isNullOrWhitespace(column.id)) { throw new Error("column.id must be non-empty unique string."); }
        if (lookup[column.id] !== undefined) { throw new Error("column.id must be unique. `".concat(column.id, "` is a duplicate.")); }
        lookup[column.id] = column;
        if (stringUtil.isNullOrWhitespace(column.field)) { throw new Error("column.field must be non-empty string."); }
        if (column.comparer !== undefined && typeof column.comparer !== "function" && stringUtil.isNullOrWhitespace(column.comparer)) {
            throw new Error("column.comparer must be a string or function.");
        }
        if (column.group !== undefined && !isObject(column.group) && stringUtil.isNullOrWhitespace(column.group)) {
            throw new Error("column.group must be a string or object.");
        }
        if (column.groupTotalsFormatter !== undefined && typeof column.groupTotalsFormatter !== "function" && stringUtil.isNullOrWhitespace(column.groupTotalsFormatter)) {
            throw new Error("column.groupTotalsFormatter must be a string or function.");
        }

        // default comparer if not specified
        if (column.comparer === undefined) { column.comparer = column.field.concat(":default"); }


        // Map
        var slickColumn = {
            field: column.field,
            id: column.id,
            resizable: !!column.resizable,
            sortable: !!column.sortable
        };
        slickColumn.formatter = adaptFormatter(
            factories.formatter(column, column.formatter !== undefined ? column.formatter : column.field.concat(":default")),
            factories.formatter(column, column.groupTotalsFormatter !== undefined ? column.groupTotalsFormatter : column.field.concat(":aggregate"))
        );
        if (column.cssClass !== undefined) { slickColumn.cssClass = column.cssClass; }
        if (column.filterable !== undefined) { slickColumn.filterable = !!column.filterable; }
        if (column.filtered !== undefined) { slickColumn.filtered = !!column.filtered; }
        if (column.headerCssClass !== undefined) { slickColumn.headerCssClass = column.headerCssClass; }
        if (column.maxWidth !== undefined) { slickColumn.maxWidth = column.maxWidth; }
        if (column.minWidth !== undefined) { slickColumn.minWidth = column.minWidth; }
        if (column.title !== undefined || column.titleIcon !== undefined) {
            slickColumn.name = column.titleIcon
                ? "<i class='" + column.titleIcon + "'></i>"
                : column.title;
        }
        if (column.title !== undefined && column.titleIcon !== undefined) { slickColumn.toolTip = column.title; }
        if (column.width !== undefined) { slickColumn.width = column.width; }
        return slickColumn;
    }, columns);
}


//-------------------------------------------
// Transforms an array of sorted columns to the
// slickgrid representation.
function mapSortedColumns(sortedColumns, columns) {
    if (!Array.isArray(sortedColumns)) { throw new Error("sortedColumns must be an array."); }
    return R.map(function mapSortedColumn(sortedColumn) {
        if (stringUtil.isNullOrWhitespace(sortedColumn.id)) { throw new Error("sortedColumn.id must be non-empty unique string."); }
        if (!findColumn(sortedColumn.id, columns)) { throw new Error("sortedColumn.id must match a column id."); }
        return { columnId: sortedColumn.id, sortAsc: !sortedColumn.isDescending };
    }, R.filter(function (o) { return o.id !== undefined; }, sortedColumns));
}


//-------------------------------------------
// Resolves selectedRows and selectedItems.
// Skips any items that cannot be found.
function resolveSelected(selectedItems, view, idField) {
    if (!Array.isArray(selectedItems)) { throw new Error("selectedItems must be an array."); }
    var i, item, selectedItem, id,
        l = selectedItems.length,
        items = [],
        ids = {};
    for (i = 0; i < l; i += 1) {
        selectedItem = selectedItems[i];
        if (!isNullOrUndefined(selectedItem) && !isNullOrUndefined(selectedItem[idField])) {
            id = selectedItem[idField];
            item = view.getItemById(id);
            if (!isNullOrUndefined(item)) {
                items.push(item);
                ids[id] = true;
            }
        }
    }
    return { ids: ids, items: items };
}


//-------------------------------------------
// Resolves an array of sorted columns into
// sorted options (comparer function) and
// grouping options (array of group definitions)
// using the built-in factories.
function resolveSortedColumns(columns, factories, sortedColumns, multiSelect) {
    var i, column, sortedColumn, groups = [],
        comparators = [],
        l = sortedColumns.length;

    for (i = 0; i < l; i += 1) {
        // if the sorted column contains both a column id and comparer/group properties, then the comparer/group
        // from the sorted column takes precedence over the comparer/group from the column definition
        sortedColumn = sortedColumns[i];
        column = (sortedColumn.id !== undefined ? findColumn(sortedColumn.id, columns) : null);
        if (sortedColumn.comparer !== undefined || sortedColumn.group !== undefined) {
            if (sortedColumn.comparer !== undefined) {
                comparators.push(factories.comparer(column || sortedColumn, sortedColumn.comparer, !!sortedColumn.isDescending));
            }
            if (sortedColumn.group !== undefined) {
                groups = groups.concat(factories.group(column || sortedColumn, sortedColumn.group, !!sortedColumn.isDescending));
            }
        } else if (column) {
            if (column.comparer !== undefined) {
                comparators.push(factories.comparer(column, column.comparer, !!sortedColumn.isDescending));
            }
            if (column.group !== undefined) {
                groups = groups.concat(factories.group(column, column.group, !!sortedColumn.isDescending));
            }
        }
    }

    for (i = 0, l = groups.length; i < l; i += 1) {
        if (groups[i].displayHeaderTotals && groups[i].aggregators) {
            groups[i].headerTitleColspan = util.findFirstAggregateColumn(columns, groups[i].aggregators) + (multiSelect ? 1 : 0);
        }
    }

    return {
        comparer: (comparators.length > 0 ? comparerFactory.composeComparators(comparators) : null),
        groups: groups
    };
}


//-------------------------------------------
// Triggers a grid event that resolves a column.
function triggerColumnAction(viewModel, properties, propertyName) {
    var action = viewModel._events[propertyName];
    if (action === undefined) { return; }
    var columnId = properties[propertyName];
    if (columnId === undefined) { return; }
    action.call(viewModel, findColumn(columnId, viewModel.columns.peek()), properties.mouseX, properties.mouseY);
}


//-------------------------------------------
// Triggers a grid event that resolves an item.
function triggerItemAction(viewModel, properties, propertyName) {
    var action = viewModel._events[propertyName];
    if (action === undefined) { return; }
    var rowIndex = properties[propertyName];
    if (rowIndex === undefined) { return; }
    var item = viewModel._dataView.getItem(rowIndex);
    if (item && !item.__group && !item.__groupTotals) { action.call(viewModel, item, properties.mouseX, properties.mouseY); }
}



//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Grid ViewModel
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

//-------------------------------------------
// Initializes a new instance of the GridViewModel.
function GridViewModel(properties, events) {

    // ensure new instance of the type
    if (!(this instanceof GridViewModel)) { return new GridViewModel(properties, events); }

    // validate events
    if (events) {
        if (events.columnContext !== undefined && typeof events.columnContext !== "function") { throw new Error("events.columnContext must be a function."); }
        if (events.filterColumn !== undefined && typeof events.filterColumn !== "function") { throw new Error("events.filterColumn must be a function."); }
        if (events.defaultAction !== undefined && typeof events.defaultAction !== "function") { throw new Error("events.defaultAction must be a function."); }
        if (events.itemClick !== undefined && typeof events.itemClick !== "function") { throw new Error("events.itemClick must be a function."); }
        if (events.itemContext !== undefined && typeof events.itemContext !== "function") { throw new Error("events.itemContext must be a function."); }
    }

    // internal state
    this._dataView = new slickData.DataView();
    this._initialMetadataProvider = this._dataView.getItemMetadata;
    this._events = events;
    this._idField = "id";
    this._itemMetadataProvider = R.identity;
    this._factories = {
        aggregator: aggregatorFactory.defaultFactory,
        comparer: comparerFactory.defaultFactory,
        formatter: formatterFactory.defaultFactory,
        group: groupFactory.defaultFactory
    };
    this._multiSelect = false;
    this._pushGridState = ko.observable(); // intended to push data to slickgrid binding

    // override the dataview's metadata provider with our own
    this._dataView.getItemMetadata = this._getItemMetadata.bind(this);

    // external api (read-only observables)
    this.columns = createReadonlyObservable();
    this.focusedGroup = createReadonlyObservable(null);
    this.focusedItem = createReadonlyObservable(null);
    this.items = createReadonlyObservable();
    this.options = createReadonlyObservable();
    this.scrollTop = createReadonlyObservable();
    this.selectedItems = createReadonlyObservable();
    this.sortedColumns = createReadonlyObservable();

    if (properties !== undefined) { this.apply(properties); }
}


//-------------------------------------------
// Adds an item to an underlying array of "items" property.
// It also updates the grid. This is partial update,
// i.e. change is reflected, but whole grid is not rerendered.
GridViewModel.prototype.addItem = createItemsModifier(
    function gridViewModelAddItem(item) {
        this._dataView.addItem(item);
    }
);


//-------------------------------------------
// Deletes an item from an underlying array of "items" property.
// It also updates the grid. This is partial update,
// i.e. change is reflected, but whole grid is not rerendered.
GridViewModel.prototype.deleteItem = createItemsModifier(
    function gridViewModelDeleteItem(id) {

        // Get the current focused row (before delete)
        var focusedItem = this.focusedItem.peek();
        var focusedRow = (focusedItem
            ? this._dataView.getRowById(focusedItem[this._idField])
            : null);

        this._dataView.deleteItem(id);

        var state;
        if (focusedItem && id === focusedItem[this._idField]) {
            var row = findFocus(this._dataView, focusedRow);
            state = { focusedItem: this._dataView.getItem(row) };
        }

        if (this._multiSelect) {
            if (state) {
                state.selectedItems = this.selectedItems.peek();
            } else {
                state = { selectedItems: this.selectedItems.peek() };
            }
        }

        if (state) { this.apply(state); }
    }
);


//-------------------------------------------
// Inserts an item to an underlying array of "items" property.
// It also updates the grid. This is partial update,
// i.e. change is reflected, but whole grid is not rerendered.
GridViewModel.prototype.insertItem = createItemsModifier(
    function gridViewModelInsertItem(index, item) {
        this._dataView.insertItem(index, item);
    }
);


//-------------------------------------------
// Updates an item in an underlying array of "items" property.
// It also updates the grid. This is partial update,
// i.e. change is reflected, but whole grid is not rerendered.
GridViewModel.prototype.updateItem = createItemsModifier(
    function gridViewModelUpdateItem(id, item) {
        this._dataView.updateItem(id, item);
    }
);


//-------------------------------------------
// Applies specified properties to the grid.
// Any properties that are `undefined` are ignored.
GridViewModel.prototype.apply = createInternalUpdateGuard(function gridViewModelApply(properties) {

    if (!isObject(properties)) { throw new Error("apply properties must be an object."); }
    if (properties.focusedGroup !== undefined) { throw new Error("Setting 'focusedGroup' programmatically is not supported."); }

    // 1. Initialize factories
    if (properties.factories !== undefined) {
        this._factories = {
            aggregator: (properties.factories.aggregator !== undefined ? aggregatorFactory.init(properties.factories.aggregator) : aggregatorFactory.defaultFactory),
            comparer: (properties.factories.comparer !== undefined ? comparerFactory.init(properties.factories.comparer) : comparerFactory.defaultFactory),
            formatter: (properties.factories.formatter !== undefined ? formatterFactory.init(properties.factories.formatter) : formatterFactory.defaultFactory)
        };
        this._factories.group = (properties.factories.group !== undefined
            ? groupFactory.init(properties.factories.group, this._factories.aggregator, this._factories.comparer, this._factories.formatter)
            : groupFactory.defaultFactory);
    }



    // 2. Validate & initialize properties
    var state = {}, options = {}, optionsAffected = false;
    if (this.options.peek() === undefined && this._events) {
        if (this._events.columnContext !== undefined) { options.supportColumnContext = true; optionsAffected = true; }
        if (this._events.itemContext !== undefined) { options.supportItemContext = true; optionsAffected = true; }
        if (this._events.filterColumn !== undefined) { options.supportFilterColumn = true; optionsAffected = true; }
    }
    if (properties.autoHeight !== undefined) { options.autoHeight = !!properties.autoHeight; optionsAffected = true; }
    if (properties.disabled !== undefined) {
        var focusedItem = this.focusedItem.peek();
        if (!properties.disabled && properties.focusedItem === undefined && focusedItem) {
            state.focusedRow = this._dataView.getRowById(focusedItem[this._idField]);
        }
        options.disabled = !!properties.disabled;
        optionsAffected = true;
    }
    if (properties.forceFitColumns !== undefined) { options.forceFitColumns = !!properties.forceFitColumns; optionsAffected = true; }
    if (properties.shortcutActions !== undefined) { options.shortcutActions = properties.shortcutActions; optionsAffected = true; }
    if (properties.multiSelect !== undefined) { options.multiSelect = !!properties.multiSelect; this._multiSelect = options.multiSelect; optionsAffected = true; }
    if (properties.idField !== undefined) {
        if (stringUtil.isNullOrWhitespace(properties.idField) && (typeof properties.idField !== "number" || isNaN(properties.idField))) { throw new Error("idField must be a non-empty string or number."); }
        this._idField = properties.idField;
        options.idField = properties.idField;
        optionsAffected = true;
    }
    if (properties.rowCssProvider !== undefined) {
        if (properties.rowCssProvider !== null && stringUtil.isNullOrWhitespace(properties.rowCssProvider) && typeof properties.rowCssProvider !== "function") {
            throw new Error("rowCssProvider must be a non-empty string, null or a function.");
        }
        options.rowCssProvider = properties.rowCssProvider;
        optionsAffected = true;

        this._itemMetadataProvider = options.rowCssProvider === null
            ? R.identity
            : this._createRowCssProvider(properties.rowCssProvider).bind(this);
    }
    if (optionsAffected) { state.options = R.merge(this.options.peek(), options); }
    if (properties.action !== undefined) { state.action = properties.action; }
    if (properties.columns !== undefined) { state.columns = mapColumns(properties.columns, this._factories); }
    if (properties.invalidate !== undefined) { state.invalidate = properties.invalidate; }
    if (properties.items !== undefined && !Array.isArray(properties.items)) { throw new Error("items must be an array."); }
    if (properties.resize !== undefined) { state.resize = !!properties.resize; }
    if (properties.scrollTop !== undefined) {
        if (typeof properties.scrollTop !== "number" || isNaN(properties.scrollTop)) { throw new Error("scrollTop must be a number."); }
        state.scrollTop = properties.scrollTop;
    }
    if (properties.sortedColumns !== undefined) { state.sortCols = mapSortedColumns(properties.sortedColumns, (properties.columns || this.columns.peek() || [])); }



    // 3. Apply changes to slickgrid view-model (sorting, grouping, ...)
    var sortedColumns = properties.sortedColumns || this.sortedColumns.peek();
    var sortColumnsAffected = sortedColumns && (
        properties.columns !== undefined ||
        properties.factories !== undefined ||
        properties.sortedColumns !== undefined
    );
    var itemsAffected = properties.items !== undefined;
    var metadataProviderAffected = properties.rowCssProvider !== undefined;
    if (sortColumnsAffected || itemsAffected || metadataProviderAffected) {
        this._dataView.beginUpdate();
        // set items
        if (itemsAffected) {
            this._dataView.setItems(properties.items, this._idField);
        }
        if (sortColumnsAffected) {
            var resolved = resolveSortedColumns(
                properties.columns || this.columns.peek() || [],
                this._factories,
                sortedColumns,
                this._multiSelect
            );
            if (resolved.comparer !== null) { this._dataView.sort(resolved.comparer); }
            this._dataView.setGrouping(resolved.groups);
        }
        this._dataView.endUpdate();
    }

    // Notify slick.grid of changes (needs to happen after _dataView is updated)

    // 4a. Resolve focused item
    var resolvedFocusedItem;
    if (properties.focusedItem !== undefined) {
        if (properties.focusedItem === null) {
            state.focusedRow = null;
            resolvedFocusedItem = null;
        } else if (!isNullOrUndefined(properties.focusedItem[this._idField])) {
            var focusedId = properties.focusedItem[this._idField];
            state.focusedRow = this._dataView.getRowById(focusedId);
            resolvedFocusedItem = this._dataView.getItemById(focusedId);
        } else {
            throw new Error("focusedItem id must be defined and not null.");
        }
    }

    // 4b. Resolve selected items (disable selectedItems for single-select)
    var resolvedSelected;
    if (this._multiSelect && properties.selectedItems !== undefined) {
        resolvedSelected = resolveSelected(properties.selectedItems, this._dataView, this._idField);
        state.selectedIds = resolvedSelected.ids;
    } else if (!this._multiSelect) {
        // clear the selectedItems when switching to multi-select
        var selectedItems = this.selectedItems.peek();
        if (selectedItems && selectedItems.length > 0) { resolvedSelected = { items: [] }; }
        if (resolvedFocusedItem) {
            state.selectedIds = R.fromPairs([[resolvedFocusedItem[this._idField], true]]);
        }
    }



    // 4c. Notify slickgrid state changes
    this._pushGridState(state);



    // 5. Notify affected observables
    if (properties.columns !== undefined) { this.columns._innerObservable(properties.columns); }
    if (resolvedFocusedItem !== undefined) { this.focusedItem._innerObservable(resolvedFocusedItem); }
    if (properties.items !== undefined) { this.items._innerObservable(properties.items); }
    if (optionsAffected) { this.options._innerObservable(state.options); }
    if (properties.scrollTop !== undefined) { this.scrollTop._innerObservable(properties.scrollTop); }
    if (properties.sortedColumns !== undefined) { this.sortedColumns._innerObservable(properties.sortedColumns); }
    if (resolvedSelected !== undefined) { this.selectedItems._innerObservable(resolvedSelected.items); }

});


//-------------------------------------------
// Applies grid state to the view-model.
GridViewModel.prototype._applyGridState = createInternalUpdateGuard(function applyGridState(properties) {

    // Looks up the focused item and focused group using
    // the focusedRow sent from slick.grid.
    if (properties.focusedRow !== undefined) {
        var focusedItem = null;
        var focusedGroup = null;
        if (properties.focusedRow !== null) {
            var item = this._dataView.getItem(properties.focusedRow);
            if (!item.__nonDataRow) { // item is a data row
                focusedItem = item;
            } else if (item.__group) {
                focusedGroup = item;
            }
        }
        this.focusedItem._innerObservable(focusedItem);
        this.focusedGroup._innerObservable(focusedGroup);
    }


    // Updates the order and width with those
    // sent from slick.grid.
    if (properties.columns !== undefined) {
        var current = this.columns.peek();
        var columns = R.map(
            function (column) {
                var actual = findColumn(column.id, current);
                if (column.width !== undefined) { actual.width = column.width; }
                return actual;
            },
            R.filter(R.complement(R.propEq("id", "_checkbox")), properties.columns)
        );
        this.columns._innerObservable(columns);
    }

    if (properties.scrollTop !== undefined) { this.scrollTop._innerObservable(properties.scrollTop); }


    // Updates with the sort columns sent from slick.grid
    // and apply sorting and grouping to the data view.
    if (properties.sortCols !== undefined || properties.columns !== undefined) {
        var sortedColumns = (properties.sortCols
            ? R.map(function (column) {
                return {
                    id: column.sortCol.id,
                    isDescending: !column.sortAsc
                };
            }, properties.sortCols)
            : this.sortedColumns.peek());
        var resolved = resolveSortedColumns(
            this.columns.peek() || [],
            this._factories,
            sortedColumns || [],
            this._multiSelect
        );
        if (resolved.comparer !== null) { this._dataView.sort(resolved.comparer); }
        this._dataView.setGrouping(resolved.groups);
        this.sortedColumns._innerObservable(sortedColumns);
    }

    // Looks up the selected items using the selectedIds object
    // example. => { "task1": true, "task2": false, "task3": true }
    // would result in an array containing items identified by `task1` and `task3`.
    // Ignore for single-selection mode.
    if (this._multiSelect && properties.selectedIds !== undefined) {
        this.selectedItems._innerObservable(
            R.map(
                this._dataView.getItemById,
                R.filter(R.prop(R.__, properties.selectedIds), R.keys(properties.selectedIds))
            )
        );
    }


    // Handle events
    if (this._events) {
        triggerColumnAction(this, properties, "columnContext");
        triggerColumnAction(this, properties, "filterColumn");
        triggerItemAction(this, properties, "defaultAction");
        triggerItemAction(this, properties, "itemClick");
        triggerItemAction(this, properties, "itemContext");
    }
});

//-------------------------------------------
// Called by the groupselectionplugin's metadata provider
GridViewModel.prototype._getItemMetadata = function getItemMetadata(row) {
    // call the initial metadata provider
    // from the dataview to build the
    // full metadata object which could contain
    // a lot more information than row css.
    var metadata = this._initialMetadataProvider(row);
    return this._itemMetadataProvider(metadata, row);
};


//-------------------------------------------
// Creates a function to provide row css to the slickgrid's dataview
// at render time.
GridViewModel.prototype._createRowCssProvider = function createRowCssProvider(rowCssProvider) {
    if (rowCssProvider === null) { return this._initialMetadataProvider; }

    var isProviderPath = typeof rowCssProvider === "string";
    var getPath = isProviderPath ? R.path(rowCssProvider.split(".")) : null;
    var getPathCss = function getPathCss(row) {
        var property = getPath(row);
        if (property === undefined) {
            return "";
        }
        return property;
    };
    var getCallbackCss = function getCallbackCss(row) {
        var result = rowCssProvider(row);
        if (!R.isArrayLike(result)) { throw new Error("Expected rowCssProvider result to be array-like."); }
        return R.join(" ", result);
    };
    var getCss = isProviderPath
        ? getPathCss
        : getCallbackCss;

    return function getRowMetadata(metadata, row) {
        if (row !== null) {
            var item = this._dataView.getItem(row);

            // append any css from the rowCssProvider onto what may be added by the original metadata provider.
            var metadataClasses = (metadata && metadata.cssClasses) || "";
            metadata = R.merge(metadata, {
                cssClasses: metadataClasses + getCss(item) + " " // pad with a space for the next metadata provider (group selection plugin) is expecting it.
            });
        }
        return metadata;
    };
};


module.exports = GridViewModel;

// Export for TypeScript consumers
module.exports.GridViewModel = GridViewModel;




//////////////////
// WEBPACK FOOTER
// ./~/pli/ko-grid/knockoutGridViewModel.js
// module id = 414
// module chunks = 49